import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LABEL, LINK_DESCRIPTION, PAST_SESSIONS_ENABLED } from '../../../../../../../modules/passport-pages/config';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../global/text/text';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { CustomFieldsOptionsListDTO, DefaultPageType, DefaultPublicPages, ReplaysPageLayoutOptions, SessionPageLayoutOptions, UpcomingSessionsTypeOptions } from '../../../../../../../api/custom-pages.dao';
import { IItemProps } from '../../../../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { DefaultPageFields } from './_default-page-fields';
import { DefaultPageViewModel } from './default-page.viewmodel';
import { InfoPopover } from '../../../../../../global/info-popover/info-popover';
import { SpeakersPageFields } from './_default-page-fields/speakers-page.fields';
import { APPLY_AND_CLOSE_TEXT, APPLY_TEXT } from '../../../../../../../constants/common-labels.constants';


export type Props = {
    pageVm: DefaultPageViewModel;
    onCancel: () => void;
    onApply: () => Promise<void>;
    onApplyAndClose: () => Promise<void>;
    sessionsLayoutOptions?: SessionPageLayoutOptions[];
    upcomingSessionsTypeOptions?: UpcomingSessionsTypeOptions[];
    replaysLayoutOptions?: ReplaysPageLayoutOptions[];
    organizationTypes?: IItemProps[];
    customFieldsOptionsList?: CustomFieldsOptionsListDTO[];
};


export const DefaultPagePresentation = observer((props: Props): JSX.Element => {
    const { formatMessage } = useIntl();
    const { pageVm } = props;

    const pageLabel = pageVm.getFieldByKey(LABEL);
    const linkDescription = pageVm.getFieldByKey(LINK_DESCRIPTION);
    const linkDescribtionEntity = pageVm.getFieldConfigByKey(LINK_DESCRIPTION);

    const applyAndClose = () => !pageVm.isValid() ? pageVm.setVmTouched() : props.onApplyAndClose();
    const apply = () => !pageVm.isValid() ? pageVm.setVmTouched() : props.onApply();

    const canBePublic = DefaultPublicPages.includes(pageVm.getType());

    let enabledSwitchLabel = {
        message: pageVm.pageName,
        id: `oe.defaultPage.${pageVm.getType()}`
    };
    if (canBePublic) {
        enabledSwitchLabel.message += ' (after sign-in)';
        enabledSwitchLabel.id = `oe.defaultPage.${pageVm.getType()}AfterSignIn`;
    }

    const publicSignInSwitchLabel = {
        message: `${pageVm.pageName} (public)`,
        id: `oe.defaultPage.${pageVm.getType()}Public`
    };

    const getPastSessionsEnabled = () => {
        const { value: pastSessionsEnabled = true } = pageVm.getFieldByKey(PAST_SESSIONS_ENABLED);

        return (<FormField>
            <div className='website-custom__checkbox-label m-bottom-0px' data-test-id='general-sessions-in-past-toggle'>
                <OEToggle
                    isSelected={pastSessionsEnabled as boolean}
                    onClick={() => pageVm.setFieldValueByKey(PAST_SESSIONS_ENABLED, !pastSessionsEnabled)}
                />
                {pastSessionsEnabled ?
                    <Text
                        message='Past sessions are displayed'
                        id='oe.defaultPage.pastSessionsAreDisplayed'
                        description='Label text'
                    /> :
                    <Text
                        message='Past sessions are hidden'
                        id='oe.defaultPage.pastSessionsAreHidden'
                        description='Label text'
                    />
                }
            </div>
        </FormField>);
    };
    return (
        <CommonPopupBodyFooterLayout
            className='custom-page-modal'
            body={
                <form className='oe-form'>
                    <div className='oe-form__body'>
                        <FormField>
                            <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                                <OEToggle
                                    isSelected={pageVm.isEnabled()}
                                    onClick={pageVm.togglePage}
                                    isDisabled={pageVm.isToggleDisabled}
                                    dataTestId='default-page-enabled-toggle'
                                />
                                {pageVm.isEnabled()
                                    ? <Text
                                        message={`${enabledSwitchLabel.message} is enabled`}
                                        id={`${enabledSwitchLabel.id}.enabled`}
                                        description='Label text'
                                    />
                                    : <Text
                                        message={`${enabledSwitchLabel.message} is disabled`}
                                        id={`${enabledSwitchLabel.id}.disabled`}
                                        description='Label text'
                                    />
                                }

                            </div>
                        </FormField>
                        {canBePublic && <FormField>
                            <div className='website-custom__checkbox-label m-bottom-0px'>
                                <OEToggle
                                    isSelected={pageVm.isPublicEnabled()}
                                    onClick={pageVm.togglePublicPage}
                                    dataTestId='default-page-public-enabled-toggle'
                                />
                                {pageVm.isPublicEnabled()
                                    ? <Text
                                        message={`${publicSignInSwitchLabel.message} is enabled`}
                                        id={`${publicSignInSwitchLabel.id}.enabled`}
                                        description='Label text'
                                    />
                                    : <Text
                                        message={`${publicSignInSwitchLabel.message} is disabled`}
                                        id={`${publicSignInSwitchLabel.id}.disabled`}
                                        description='Label text'
                                    />
                                }

                            </div>
                        </FormField>}
                        <FormField label={formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor, { prefix: pageVm.pageName })}>
                            <div className='flex'>
                                <input
                                    className='oe-form-field__control'
                                    onBlur={pageVm.setValueTouched.bind(pageVm, LABEL)}
                                    value={pageLabel.value as string}
                                    type='text'
                                    onChange={e => pageVm.setFieldValueByKey(LABEL, e.target.value)}
                                />
                            </div>
                            <span className='oe-form-field__error'>{
                                !pageLabel.valid && pageLabel.touched &&
                                <FormattedMessage
                                    values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor, { prefix: pageVm.pageName }) }}
                                    {...pageLabel.validationMessage}
                                />
                            }</span>
                        </FormField>
                        {pageVm.getType() === DefaultPageType.Speakers &&
                            <SpeakersPageFields pageVm={pageVm} />
                        }
                        {pageVm.getType() === DefaultPageType.Session && getPastSessionsEnabled()}
                        {pageVm.getType() !== DefaultPageType.Welcome &&
                            <>
                                <FormField>
                                    <div className='website-custom__checkbox-label m-bottom-0px'>
                                        <OEToggle
                                            isDisabled={!pageVm.isEnabled()}
                                            isSelected={pageVm.isPageLinkEnabled()}
                                            onClick={pageVm.togglePageLinkEnabled}
                                            dataTestId='welcome-page-link-toggle'
                                        />
                                        <Text
                                            message='Show link on Welcome page'
                                            id='oe.defaultPages.showLinkOnWelcomePage'
                                            description='Label text'
                                        />
                                    </div>
                                </FormField>
                                <FormField
                                    label={
                                        <div className='flex m-bottom-6px'>
                                            {formatMessage(linkDescribtionEntity.labelDescriptor, { prefix: pageVm.pageName })}
                                            {linkDescribtionEntity.fieldInfoMessage[pageVm.pageName] &&
                                                <InfoPopover className='m-left-8px'>
                                                    {linkDescribtionEntity.fieldInfoMessage[pageVm.pageName]}
                                                </InfoPopover>
                                            }

                                        </div>
                                    }
                                >
                                    <div className='flex'>
                                        <input
                                            className='oe-form-field__control'
                                            onBlur={pageVm.setValueTouched.bind(pageVm, LINK_DESCRIPTION)}
                                            value={linkDescription.value as string || ''}
                                            type='text'
                                            onChange={e => pageVm.setFieldValueByKey(LINK_DESCRIPTION, e.target.value)}
                                        />
                                    </div>
                                    <span className='oe-form-field__error'>{
                                        !linkDescription.valid && linkDescription.touched &&
                                        <FormattedMessage
                                        values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor, { prefix: pageVm.pageName }) }}
                                            {...linkDescription.validationMessage}
                                        />
                                    }</span>
                                </FormField>
                            </>}

                        <DefaultPageFields
                            pageVm={pageVm}
                            sessionsLayoutOptions={props.sessionsLayoutOptions}
                            organizationTypes={props.organizationTypes}
                            customFieldsOptionsList={props.customFieldsOptionsList}
                            upcomingSessionsTypeOptions={props.upcomingSessionsTypeOptions}
                            replaysLayoutOptions={props.replaysLayoutOptions}
                        />
                    </div>
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        text={formatMessage(APPLY_AND_CLOSE_TEXT)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={applyAndClose}
                    />
                    <DefaultButton
                        text={formatMessage(APPLY_TEXT)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={apply}
                    />
                    <DefaultCloseSubWindowButton />
                </div>
            }
        />
    );
});
